import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useLocation } from '@tanstack/react-router';
import { DueBadge } from '@/features/memorization/components/due-badge';
import {
  ReaderNavigationTrigger,
  ReaderNavigationContent,
} from '@/features/navigation/components/reader-navigation-menu';
import {
  SettingsTrigger,
  SettingsContent,
} from '@/features/navigation/components/settings-menu';
import { useChromeStoreContext } from '@/features/reader/stores/chrome-visibility';
import { cn } from '@/shared/lib/utils';
import { smooth } from '@/shared/lib/animation';

type Panel = 'navigation' | 'settings';

export const MenuSkeleton = () => (
  <nav className="absolute bottom-3 left-1/2 -translate-x-1/2 z-40 w-[min(24rem,calc(100vw-1.5rem))] rounded-3xl bg-background/80 backdrop-blur-xl border border-border/30 shadow-lg shadow-black/5 overflow-hidden">
    <div className="flex items-center justify-center gap-2 px-3 py-2.5">
      <div className="h-12 w-12 rounded-full bg-muted/50 animate-pulse" />
      <div className="w-px h-5 bg-border/30" />
      <span className="inline-flex items-center gap-1.5 rounded-full border border-transparent px-3.5 py-2 text-sm font-medium">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-muted/50" />
        <span className="invisible">Review</span>
      </span>
      <div className="w-px h-5 bg-border/30" />
      <div className="h-12 w-12 rounded-full bg-muted/50 animate-pulse" />
    </div>
  </nav>
);

interface MenuProps {
  ratingRow?: ReactNode;
}

export const Menu = ({ ratingRow }: MenuProps) => {
  const [activePanel, setActivePanel] = useState<Panel | null>(null);
  const activePanelRef = useRef<Panel | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  const chromeStore = useChromeStoreContext();

  // Wrapper that pins/unpins chrome synchronously (no effect delay)
  const setPanel = useCallback(
    (next: Panel | null) => {
      const prev = activePanelRef.current;
      activePanelRef.current = next;
      setActivePanel(next);
      if (next != null && prev == null)
        chromeStore.getState().pin('menu-expanded');
      if (next == null && prev != null)
        chromeStore.getState().unpin('menu-expanded');
    },
    [chromeStore],
  );

  // Close panel when rating row appears (adjust state during render)
  const hasRatingRow = ratingRow != null;
  const [prevHasRatingRow, setPrevHasRatingRow] = useState(hasRatingRow);
  if (hasRatingRow !== prevHasRatingRow) {
    setPrevHasRatingRow(hasRatingRow);
    if (hasRatingRow) {
      setPanel(null);
    }
  }

  const closePanel = useCallback(() => setPanel(null), [setPanel]);

  const toggle = useCallback(
    (panel: Panel) => {
      const next = activePanelRef.current === panel ? null : panel;
      setPanel(next);
    },
    [setPanel],
  );

  // Close panel on route change
  useEffect(() => {
    if (location.pathname !== pathnameRef.current) {
      setPanel(null);
      pathnameRef.current = location.pathname;
    }
  }, [location.pathname, setPanel]);

  // Close panel on Escape key
  useEffect(() => {
    if (activePanel == null) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setPanel(null);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activePanel, setPanel]);

  // Close panel on click outside
  useEffect(() => {
    if (activePanel == null) return;
    function handlePointerDown(e: PointerEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setPanel(null);
      }
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [activePanel, setPanel]);

  const expandedKey = activePanel ?? (ratingRow != null ? 'rating' : null);

  return (
    <nav
      ref={navRef}
      className={cn(
        'absolute bottom-3 left-1/2 -translate-x-1/2 z-40 w-[min(24rem,calc(100vw-1.5rem))] rounded-3xl bg-background/80 backdrop-blur-xl border border-border/30 shadow-lg shadow-black/5 overflow-hidden [view-transition-name:bottom-menu]',
        'transition-transform duration-300 ease-out group-[[data-chrome-hidden]]/chrome:translate-y-[calc(100%+1rem)] group-[[data-chrome-hidden]]/chrome:pointer-events-none',
      )}
    >
      {/* Expanded panel or rating row — animates in/out */}
      <AnimatePresence initial={false}>
        {expandedKey != null && (
          <motion.div
            key={expandedKey}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={smooth}
            className="overflow-hidden"
          >
            {activePanel === 'navigation' && (
              <ReaderNavigationContent onNavigate={closePanel} />
            )}
            {activePanel === 'settings' && (
              <SettingsContent onClose={closePanel} />
            )}
            {activePanel == null && ratingRow != null && ratingRow}
            <div className="mx-3 border-t border-border/30" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Nav row */}
      <div className="flex items-center justify-center gap-2 px-3 py-2.5">
        <ReaderNavigationTrigger
          isActive={activePanel === 'navigation'}
          onToggle={() => toggle('navigation')}
        />
        <div className="w-px h-5 bg-border/50" />
        <DueBadge />
        <div className="w-px h-5 bg-border/50" />
        <SettingsTrigger
          isActive={activePanel === 'settings'}
          onToggle={() => toggle('settings')}
        />
      </div>
    </nav>
  );
};
